import {
  deriveLibraryTitle,
  getLibraryItemStatusSortWeight,
  type LibraryItem,
  type LibrarySearchResult,
} from "@/lib/library";
import type { LibraryItemWithCategories } from "@/lib/libraryCategories";

export type EditableDraft = LibrarySearchResult & { notes?: string | null };
export type LibraryRecord = LibraryItemWithCategories<LibraryItem> & { excerptCount: number; workspaceIds: string[] };
export type LibrarySortOption =
  | "recent"
  | "title"
  | "title-desc"
  | "author"
  | "author-desc"
  | "published"
  | "published-asc"
  | "status";
export type LinkedExcerpt = { id: string; body_text: string; created_at: string; notes: string | null };
export type PendingLibrarySave = {
  draft: EditableDraft;
  currentId?: string;
  categoryIds: string[];
  workspaceIds?: string[];
  closeEditor: boolean;
  successMessage: string;
};

export const toDraft = (item: LibraryItem): EditableDraft => ({
  sourceProvider: item.source_provider as EditableDraft["sourceProvider"],
  sourceType: item.source_type,
  sourceId: item.source_id,
  googleBooksId: item.google_books_id,
  openLibraryId: item.open_library_id,
  title: item.title,
  subtitle: item.subtitle,
  displayTitle: item.display_title,
  authorText: item.author_text,
  authors: Array.isArray(item.authors) ? item.authors.filter((value): value is string => typeof value === "string") : [],
  publisher: item.publisher,
  publishedDate: item.published_date,
  publishedYear: item.published_year,
  languageCode: item.language_code,
  isbn10: item.isbn_10,
  isbn13: item.isbn_13,
  edition: item.edition,
  volume: item.volume,
  series: item.series,
  pageCount: item.page_count,
  coverUrl: item.cover_url,
  coverCandidates: item.cover_url ? [item.cover_url] : [],
  description: item.description,
  doi: item.doi,
  url: item.url,
  containerTitle: item.container_title,
  accessedDate: item.accessed_date,
  openAccessUrl: (item as { open_access_url?: string | null }).open_access_url ?? null,
  metadata: typeof item.metadata === "object" && item.metadata ? (item.metadata as Record<string, unknown>) : {},
  status: item.status,
  isOwned: item.is_owned ?? false,
  bookBinding: (item.book_binding as "paperback" | "hardback" | null) ?? null,
  isFirstEdition: item.is_first_edition ?? false,
  isSigned: item.is_signed ?? false,
  isNumbered: item.is_numbered ?? false,
  numberedIndex: item.numbered_index ?? null,
  numberedTotal: item.numbered_total ?? null,
  purchaseCostCents: (item as { purchase_cost_cents?: number | null }).purchase_cost_cents ?? null,
  replacementCostCents: (item as { replacement_cost_cents?: number | null }).replacement_cost_cents ?? null,
  citationStyleOverride:
    (item.citation_style_override as string | null | undefined) ?? null,
  notes: item.notes,
  completeness: {
    hasIsbn: !!(item.isbn_13 || item.isbn_10),
    hasCover: !!item.cover_url,
    hasPublisher: !!item.publisher,
    hasPageCount: !!item.page_count,
    hasDescription: !!item.description,
  },
});

const normalizeForSearch = (value: string | null | undefined) => value?.trim().toLocaleLowerCase() ?? "";
const getSortableTitle = (item: LibraryRecord) => normalizeForSearch(deriveLibraryTitle(item));
const getSortableAuthor = (item: LibraryRecord) => normalizeForSearch(item.author_text);

const getSortablePublishedDate = (item: LibraryRecord) => {
  const parsed = item.published_date ? Date.parse(item.published_date) : Number.NaN;
  if (!Number.isNaN(parsed)) return parsed;
  return item.published_year ?? Number.NEGATIVE_INFINITY;
};

export const sortItems = (items: LibraryRecord[], sortBy: LibrarySortOption = "recent") => {
  const sorted = [...items];

  switch (sortBy) {
    case "title":
      return sorted.sort((a, b) => getSortableTitle(a).localeCompare(getSortableTitle(b)) || getSortableAuthor(a).localeCompare(getSortableAuthor(b)));
    case "title-desc":
      return sorted.sort((a, b) => getSortableTitle(b).localeCompare(getSortableTitle(a)) || getSortableAuthor(a).localeCompare(getSortableAuthor(b)));
    case "author":
      return sorted.sort((a, b) => getSortableAuthor(a).localeCompare(getSortableAuthor(b)) || getSortableTitle(a).localeCompare(getSortableTitle(b)));
    case "author-desc":
      return sorted.sort((a, b) => getSortableAuthor(b).localeCompare(getSortableAuthor(a)) || getSortableTitle(a).localeCompare(getSortableTitle(b)));
    case "published":
      return sorted.sort((a, b) => getSortablePublishedDate(b) - getSortablePublishedDate(a) || getSortableTitle(a).localeCompare(getSortableTitle(b)));
    case "published-asc":
      return sorted.sort((a, b) => getSortablePublishedDate(a) - getSortablePublishedDate(b) || getSortableTitle(a).localeCompare(getSortableTitle(b)));
    case "status":
      return sorted.sort((a, b) => getLibraryItemStatusSortWeight(a.status) - getLibraryItemStatusSortWeight(b.status) || getSortableTitle(a).localeCompare(getSortableTitle(b)));
    case "recent":
    default:
      return sorted.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }
};

export const matchesLibrarySearch = (item: LibraryRecord, query: string) => {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) return true;

  const haystack = [
    item.title,
    item.display_title,
    item.subtitle,
    item.author_text,
    item.isbn_10,
    item.isbn_13,
    item.publisher,
  ]
    .map((value) => normalizeForSearch(value))
    .filter(Boolean);

  return haystack.some((value) => value.includes(normalizedQuery));
};

export type FilteredItemNoun = { singular: string; plural: string };

export const deriveFilteredItemNoun = (selectedSourceTypes: string[]): FilteredItemNoun => {
  if (selectedSourceTypes.length === 1) {
    switch (selectedSourceTypes[0]) {
      case "book": return { singular: "book", plural: "books" };
      case "journal_article": return { singular: "article", plural: "articles" };
      case "chapter": return { singular: "chapter", plural: "chapters" };
      case "proceedings_article": return { singular: "paper", plural: "papers" };
      case "website": return { singular: "website", plural: "websites" };
      case "report": return { singular: "report", plural: "reports" };
      default: return { singular: "source", plural: "sources" };
    }
  }
  return { singular: "source", plural: "sources" };
};
